// ClaimRow — one knowledge-bank claim as the person reads it
// (knowledge-bank.md §11.9; FR-13.6, FR-13.9, FR-7.2): the wording (with the
// AI marker when the agent recorded it), where it comes from ("source, p. N"
// per backing passage), and what it is linked to. Opening a passage shows the
// source at that place with the words highlighted. A passage that no longer
// resolves says so and opens nothing (never another passage).
//
// Correcting or removing a claim goes through the agent (no inline edit): the
// two buttons are intents the host maps to a prefilled chat request.
//
// `ClaimList` is a titled list of rows, with a line when it is empty.
// Pure projection, intents out; no state.

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { AiMarker } from "../AiMarker/AiMarker.js";
import { Button } from "../Button/Button.js";
import { Icon } from "../Icon/Icon.js";
import { Text } from "../Text/Text.js";
import "./ClaimRow.css";

// === View-model (the GET /v1/claims wire, parsed) ===

// One backing passage. `page` only when the source has pages.
export type ClaimPassage = {
    readonly sourceId: string;
    readonly sourceTitle: string;
    readonly exact: string;
    readonly page?: number;
    // False: the passage is no longer in the source (it was removed or
    // replaced), so there is nothing to open.
    readonly located: boolean;
};

type ClaimLinkKind = "concept" | "objective" | "audience";
export type ClaimLink = {
    readonly type: ClaimLinkKind;
    readonly id: string;
    readonly label: string;
};

export type Claim = {
    readonly claimId: string;
    readonly wording: string;
    // Recorded by the agent: the row carries the AI marker.
    readonly byAgent: boolean;
    readonly links: readonly ClaimLink[];
    readonly backing: readonly ClaimPassage[];
};

export type ClaimIntents = {
    // Open the source at the passage, the words highlighted.
    readonly onOpenPassage: (passage: ClaimPassage) => void;
    // Prefill a chat request to the agent; absent where there is no chat.
    readonly onCorrect?: (claim: Claim) => void;
    readonly onRemove?: (claim: Claim) => void;
};

// === Row ===

const PassageLine = ({
    passage,
    onOpen,
}: {
    readonly passage: ClaimPassage;
    readonly onOpen: (p: ClaimPassage) => void;
}): ReactNode => {
    const { t } = useLingui();
    const title = passage.sourceTitle;
    const page = passage.page;
    if (!passage.located)
        return (
            <Text as="span" size="xs" tone="faint">
                {t`${title}: the passage is no longer in the source`}
            </Text>
        );
    return (
        <button
            type="button"
            className="bilda-claim__source"
            onClick={() => onOpen(passage)}
        >
            <Icon name="source" size="xs" />
            <Text as="span" size="xs" tone="dim">
                {page === undefined ? title : t`${title}, p. ${page}`}
            </Text>
        </button>
    );
};

export const ClaimRow = ({
    claim,
    intents,
}: {
    readonly claim: Claim;
    readonly intents: ClaimIntents;
}): ReactNode => {
    const { t } = useLingui();
    const label: Record<ClaimLinkKind, string> = {
        concept: t`Is about`,
        objective: t`Serves`,
        audience: t`Written for`,
    };
    const kinds: readonly ClaimLinkKind[] = [
        "concept",
        "objective",
        "audience",
    ];
    const linkRows = kinds.flatMap((kind) => {
        const items = claim.links.filter((l) => l.type === kind);
        return items.length === 0
            ? []
            : [{ kind, text: items.map((l) => l.label).join(", ") }];
    });
    const { onCorrect, onRemove } = intents;
    return (
        <div className="bilda-claim">
            <Text as="p" size="sm" className="bilda-claim__wording">
                {claim.wording}
                {claim.byAgent ? (
                    <>
                        {" "}
                        <AiMarker iconOnly />
                    </>
                ) : null}
            </Text>
            {claim.backing.map((p) => (
                <PassageLine
                    key={`${p.sourceId}:${p.exact}`}
                    passage={p}
                    onOpen={intents.onOpenPassage}
                />
            ))}
            {linkRows.map((row) => (
                <Text key={row.kind} as="p" size="xs" tone="faint">
                    {label[row.kind]}: {row.text}
                </Text>
            ))}
            {onCorrect === undefined && onRemove === undefined ? null : (
                <div className="bilda-claim__actions">
                    {onCorrect === undefined ? null : (
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onCorrect(claim)}
                        >
                            {t`Correct it`}
                        </Button>
                    )}
                    {onRemove === undefined ? null : (
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onRemove(claim)}
                        >
                            {t`Remove`}
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};

// === List ===

export const ClaimList = ({
    claims,
    intents,
    empty,
}: {
    readonly claims: readonly Claim[];
    readonly intents: ClaimIntents;
    // The line shown when there are no claims; absent ⇒ nothing is shown.
    readonly empty?: string;
}): ReactNode =>
    claims.length === 0 ? (
        empty === undefined ? null : (
            <Text as="p" size="xs" tone="faint">
                {empty}
            </Text>
        )
    ) : (
        <ul className="bilda-claim-list">
            {claims.map((c) => (
                <li key={c.claimId}>
                    <ClaimRow claim={c} intents={intents} />
                </li>
            ))}
        </ul>
    );
